/**
 * Floating launcher / bubble-panel anchor — same corner keys and margin
 * fields as ometrics-ai v1 `FloatingButtonLayout` / `ChatContainer`.
 */

import { VIEWPORT_OVERFLOW_X_VAR } from './viewportOverflowAnchor.js'

const DEFAULT_POS = 'LOWER_RIGHT'

function margin(conf, key, fallback = '0px') {
  const v = conf?.[key]
  if (v == null || String(v).trim() === '') return fallback
  return String(v).trim()
}

/**
 * Right-anchored offsets are measured from the layout viewport edge,
 * which on pages with horizontal overflow sits past the visible area.
 * Adding the published overflow delta pulls the element back into view.
 * Left-anchored corners need no compensation: the visible region starts
 * at the layout viewport's left edge (`viewportOverflowAnchor.js`).
 */
function withOverflowRight(offset) {
  return `calc(${offset} + var(${VIEWPORT_OVERFLOW_X_VAR}, 0px))`
}

/**
 * @param {Record<string, unknown>} conf
 * @returns {Record<string, string | number>}
 */
export function getFloatingLauncherPositionStyle(conf) {
  const position = String(
    conf?.floatingPosition || conf?.position || DEFAULT_POS,
  ).toUpperCase()
  const mt = margin(conf, 'floatingMarginTop', conf?.marginTop ?? '0px')
  const mb = margin(conf, 'floatingMarginBottom', conf?.marginBottom ?? '0px')
  const ml = margin(conf, 'floatingMarginLeft', conf?.marginLeft ?? '0px')
  const mr = margin(conf, 'floatingMarginRight', conf?.marginRight ?? '0px')

  const base = {
    position: 'fixed',
    zIndex: 2147483647,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  }

  const positions = {
    LOWER_RIGHT: {
      bottom: mb,
      right: withOverflowRight(mr),
      top: 'auto',
      left: 'auto',
    },
    LOWER_LEFT: { bottom: mb, left: ml, top: 'auto', right: 'auto' },
    LOWER_MIDDLE: {
      bottom: mb,
      left: '50%',
      right: 'auto',
      top: 'auto',
      transform: 'translateX(-50%)',
      marginLeft: ml,
      marginRight: mr,
    },
    UPPER_RIGHT: {
      top: mt,
      right: withOverflowRight(mr),
      bottom: 'auto',
      left: 'auto',
    },
    UPPER_LEFT: { top: mt, left: ml, bottom: 'auto', right: 'auto' },
    UPPER_MIDDLE: {
      top: mt,
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      transform: 'translateX(-50%)',
      marginLeft: ml,
      marginRight: mr,
    },
  }

  return { ...base, ...(positions[position] || positions.LOWER_RIGHT) }
}

/**
 * @param {Record<string, unknown>} conf
 * @returns {{ positionStyle: Record<string, string | number>, colorStyle: { backgroundColor: string, color: string }, usePositionWrapper: boolean }}
 */
export function getFloatingLauncherStyles(conf, bubbleBg, bubbleFg) {
  const position = String(
    conf?.floatingPosition || conf?.position || DEFAULT_POS,
  ).toUpperCase()
  const usePositionWrapper =
    position === 'LOWER_MIDDLE' || position === 'UPPER_MIDDLE'
  const positionStyle = getFloatingLauncherPositionStyle(conf)
  const colorStyle = { backgroundColor: bubbleBg, color: bubbleFg }
  return { positionStyle, colorStyle, usePositionWrapper }
}

/**
 * Wrapper + inner panel so horizontal centering (`translateX(-50%)`) does not
 * fight Tailwind `scale-*` on the panel card.
 *
 * @param {Record<string, unknown>} conf
 * @returns {{ wrapperStyle: Record<string, string | number>, innerOriginClass: string }}
 */
export function getFloatingBubblePanelPlacement(conf) {
  const position = String(
    conf?.floatingPosition || conf?.position || DEFAULT_POS,
  ).toUpperCase()
  const mt = margin(conf, 'floatingMarginTop', conf?.marginTop ?? '0px')
  const mb = margin(conf, 'floatingMarginBottom', conf?.marginBottom ?? '0px')
  const ml = margin(conf, 'floatingMarginLeft', conf?.marginLeft ?? '0px')
  const mr = margin(conf, 'floatingMarginRight', conf?.marginRight ?? '0px')

  const lift = 'calc(3.5rem + 1.25rem)'
  const z = 2147483645

  const map = {
    LOWER_RIGHT: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        bottom: `calc(${mb} + ${lift})`,
        right: withOverflowRight(mr),
        top: 'auto',
        left: 'auto',
      },
      innerOriginClass: 'origin-bottom-right',
    },
    LOWER_LEFT: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        bottom: `calc(${mb} + ${lift})`,
        left: ml,
        top: 'auto',
        right: 'auto',
      },
      innerOriginClass: 'origin-bottom-left',
    },
    LOWER_MIDDLE: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        bottom: `calc(${mb} + ${lift})`,
        left: '50%',
        transform: 'translateX(-50%)',
        marginLeft: ml,
        marginRight: mr,
        top: 'auto',
        right: 'auto',
      },
      innerOriginClass: 'origin-bottom',
    },
    UPPER_RIGHT: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        top: `calc(${mt} + ${lift})`,
        right: withOverflowRight(mr),
        bottom: 'auto',
        left: 'auto',
      },
      innerOriginClass: 'origin-top-right',
    },
    UPPER_LEFT: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        top: `calc(${mt} + ${lift})`,
        left: ml,
        bottom: 'auto',
        right: 'auto',
      },
      innerOriginClass: 'origin-top-left',
    },
    UPPER_MIDDLE: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        top: `calc(${mt} + ${lift})`,
        left: '50%',
        transform: 'translateX(-50%)',
        marginLeft: ml,
        marginRight: mr,
        bottom: 'auto',
        right: 'auto',
      },
      innerOriginClass: 'origin-top',
    },
  }

  return map[position] || map.LOWER_RIGHT
}

/**
 * Placement for the free-shipping / abandoned-cart inline "slideout"
 * message. v1 parity (`InlineChatMessage` with `relative=true`): anchored
 * to the same corner as the launcher, hugging just above/below it (10px
 * gap vs. the panel's 20px), with a slide direction and arrow alignment
 * derived from the corner. RIGHT/LEFT take priority over LOWER/UPPER for
 * slide direction, matching v1's `Slide direction` logic exactly.
 *
 * @param {Record<string, unknown>} conf
 * @returns {{
 *   wrapperStyle: Record<string, string | number>,
 *   align: 'left' | 'right' | 'center',
 *   arrowSide: 'top' | 'bottom',
 *   slideFrom: 'left' | 'right' | 'top' | 'bottom',
 * }}
 */
export function getFloatingInlineMessagePlacement(conf) {
  const position = String(
    conf?.floatingPosition || conf?.position || DEFAULT_POS,
  ).toUpperCase()
  const mt = margin(conf, 'floatingMarginTop', conf?.marginTop ?? '0px')
  const mb = margin(conf, 'floatingMarginBottom', conf?.marginBottom ?? '0px')
  const ml = margin(conf, 'floatingMarginLeft', conf?.marginLeft ?? '0px')
  const mr = margin(conf, 'floatingMarginRight', conf?.marginRight ?? '0px')

  const lift = 'calc(3.5rem + 0.625rem)'
  const z = 2147483646

  const isRight = position.includes('RIGHT')
  const isLeft = position.includes('LEFT')
  const isLower = position.includes('LOWER')
  const align = isRight ? 'right' : isLeft ? 'left' : 'center'
  const arrowSide = isLower ? 'bottom' : 'top'
  const slideFrom = isRight ? 'right' : isLeft ? 'left' : isLower ? 'bottom' : 'top'

  const map = {
    LOWER_RIGHT: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        bottom: `calc(${mb} + ${lift})`,
        right: withOverflowRight(mr),
        top: 'auto',
        left: 'auto',
      },
    },
    LOWER_LEFT: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        bottom: `calc(${mb} + ${lift})`,
        left: ml,
        top: 'auto',
        right: 'auto',
      },
    },
    LOWER_MIDDLE: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        bottom: `calc(${mb} + ${lift})`,
        left: '50%',
        transform: 'translateX(-50%)',
        marginLeft: ml,
        marginRight: mr,
        top: 'auto',
        right: 'auto',
      },
    },
    UPPER_RIGHT: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        top: `calc(${mt} + ${lift})`,
        right: withOverflowRight(mr),
        bottom: 'auto',
        left: 'auto',
      },
    },
    UPPER_LEFT: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        top: `calc(${mt} + ${lift})`,
        left: ml,
        bottom: 'auto',
        right: 'auto',
      },
    },
    UPPER_MIDDLE: {
      wrapperStyle: {
        position: 'fixed',
        zIndex: z,
        top: `calc(${mt} + ${lift})`,
        left: '50%',
        transform: 'translateX(-50%)',
        marginLeft: ml,
        marginRight: mr,
        bottom: 'auto',
        right: 'auto',
      },
    },
  }

  const { wrapperStyle } = map[position] || map.LOWER_RIGHT
  return { wrapperStyle, align, arrowSide, slideFrom }
}
